import { IconFaceId, IconMapPin, IconQrcode, IconSparkles, IconWifi, type Icon } from '@tabler/icons-react';
import { useState } from 'react';
import { useT } from '@hana/i18n';
import { useToast } from '@hana/ui';
import './ask.i18n';
import s from './ask.module.css';

const METHODS: { k: string; icon: Icon; auto: boolean }[] = [
  { k: 'gps', icon: IconMapPin, auto: true },
  { k: 'wifi', icon: IconWifi, auto: true },
  { k: 'qr', icon: IconQrcode, auto: false },
  { k: 'face', icon: IconFaceId, auto: false },
];

/** "Cách chấm" trong thẻ chấm công hôm nay (board Mint-Mee › ckMs). */
export function CheckMethods() {
  const t = useT();
  const toast = useToast();
  const [on, setOn] = useState('gps');
  const label = (k: string) => (k === 'face' ? t('meeask.faceName') : k === 'gps' ? 'GPS' : k === 'wifi' ? 'Wifi' : 'QR');
  return (
    <div className={s.methods} role="group" aria-label={t('meeask.methods')}>
      <span>{t('meeask.methodsLb')}</span>
      {METHODS.map(({ k, icon: I, auto }) => (
        <button
          key={k}
          type="button"
          className={s.chipBtn}
          aria-pressed={on === k}
          title={t(`meeask.${k}Tip`)}
          onClick={() => {
            setOn(k);
            if (!auto) toast({ msg: t('meeask.sheet', { m: label(k) }) });
          }}
        >
          <I size={14} stroke={1.9} aria-hidden="true" />
          {label(k)}
          {auto && <span className={s.dot} aria-label={t('meeask.ready')} />}
        </button>
      ))}
    </div>
  );
}

const norm = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();

/** Hỏi HANA · quy chế (board Mint-Mee, bổ sung 03/Nov) — trả lời cố định theo từ khoá, chỉ dữ liệu của chính mình. */
export function AskPolicy({ left, pending, go }: { left: string; pending: string; go: (act: 'leave' | 'time' | 'pay' | 'ot' | 'fix') => void }) {
  const t = useT();
  const [text, setText] = useState('');
  const [asked, setAsked] = useState('');
  const [pick, setPick] = useState<number | 'other' | 'none' | null>(null);
  const run = (q: string) => {
    const n = norm(q);
    if (!n.trim()) return;
    const other = /luong|thu nhap|thuong/.test(n) && /(maittn|phuong|tuannt|nhi|nguoi khac|dong nghiep)/.test(n);
    setAsked(q);
    setPick(other ? 'other' : /phep/.test(n) ? 0 : /muon|tre/.test(n) ? 1 : /them gio|tang ca|\bot\b/.test(n) ? 2 : /quen|cham cong/.test(n) ? 3 : 'none');
  };
  const ACT = ['leave', 'time', 'ot', 'fix'] as const;
  const ans =
    pick === null
      ? null
      : pick === 'other'
        ? { a: t('meeask.aOther'), src: t('meeask.sOther'), k: t('meeask.kOther'), f: () => go('pay') }
        : pick === 'none'
          ? { a: t('meeask.aNone'), src: t('meeask.sNone'), k: t('meeask.kNone'), f: () => go('time') }
          : { a: t(`meeask.a${pick}`, { l: left, p: pending }), src: t(`meeask.s${pick}`), k: t(`meeask.k${pick}`), f: () => go(ACT[pick] ?? "time") };
  return (
    <section className={`${s.ask}`} aria-label={t('meeask.aria')} data-ask="1">
      <div className={s.top}>
        <span className={s.logo} aria-hidden="true"><IconSparkles size={16} /></span>
        <b>{t('meeask.title')}</b>
        <span>{t('meeask.scope')}</span>
      </div>
      <div className={s.row}>
        <label className={s.field}>
          <input aria-label={t('meeask.input')} value={text} placeholder={t('meeask.ph')} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') run(text); }} />
        </label>
        <button type="button" className={s.go} onClick={() => run(text)}><IconSparkles size={16} aria-hidden="true" />{t('meeask.ask')}</button>
      </div>
      <div className={s.chips}>
        {[0, 1, 2, 3].map((i) => (
          <button key={i} type="button" className={s.chipBtn} aria-pressed={pick === i} onClick={() => { setPick(i); setAsked(t(`meeask.q${i}`)); }}>{t(`meeask.q${i}`)}</button>
        ))}
      </div>
      {ans && (
        <div className={s.ans} role="status">
          <div>{asked}</div>
          <div>{ans.a}</div>
          <div className={s.foot}>
            <span>{t('meeask.src', { s: ans.src })}</span>
            <button type="button" className={s.plain} onClick={() => { setPick(null); setText(''); setAsked(''); }}>{t('meeask.clear')}</button>
            <button type="button" className={s.act} onClick={ans.f}>{ans.k}</button>
          </div>
        </div>
      )}
    </section>
  );
}
